import { Box, Stack, Typography } from '@mui/material';
import { keyframes } from '@mui/material/styles';
import { useLocation } from 'react-router-dom';
import type { ReactNode } from 'react';
import { bannerGradient, sectionForPath } from '../../theme/sectionColors';

type Props = {
  title: string;
  subtitle?: string;
  actions?: ReactNode;
};

const drift = keyframes`0%,100%{transform:translate(0,0) scale(1)}50%{transform:translate(-24px,16px) scale(1.12)}`;

/** Aurora banner shared by every page — same look as the dashboard hero, just shorter. */
const PageHeader = ({ title, subtitle, actions }: Props) => {
  const { pathname } = useLocation();
  return (
  <Box
    className="qs-animate-in"
    sx={{
      position: 'relative',
      overflow: 'hidden',
      color: '#fff',
      mb: 3,
      px: { xs: 2.5, md: 3.5 },
      py: { xs: 2.25, md: 2.75 },
      borderRadius: '24px',
      background: bannerGradient(sectionForPath(pathname)),
      backgroundSize: '200% 200%',
      boxShadow: '0 18px 40px rgba(0,0,0,.22), inset 0 0 0 1px rgba(255,255,255,.25)',
      '@media (prefers-reduced-motion: no-preference)': { animation: 'qsGradientShift 14s ease infinite' },
    }}
  >
    <Box
      sx={{
        position: 'absolute', width: 260, height: 260, right: -50, top: -110, borderRadius: '50%',
        background: 'radial-gradient(circle, rgba(255,214,122,.6), transparent 65%)',
        '@media (prefers-reduced-motion: no-preference)': { animation: `${drift} 12s ease-in-out infinite` },
      }}
    />
    <Box
      sx={{
        position: 'absolute', inset: 0, pointerEvents: 'none',
        background: 'linear-gradient(180deg, rgba(255,255,255,.22) 0%, rgba(255,255,255,0) 45%)',
      }}
    />
    <Stack
      direction={{ xs: 'column', sm: 'row' }}
      alignItems={{ xs: 'flex-start', sm: 'center' }}
      justifyContent="space-between"
      spacing={2}
      sx={{ position: 'relative' }}
    >
      <Box sx={{ minWidth: 0 }}>
        <Typography
          variant="h5"
          sx={{ fontWeight: 900, fontSize: { xs: 24, md: 30 }, lineHeight: 1.15, textShadow: '0 3px 14px rgba(0,0,0,.25)' }}
        >
          {title}
        </Typography>
        {subtitle && (
          <Typography variant="body2" sx={{ mt: 0.5, opacity: 0.92 }}>
            {subtitle}
          </Typography>
        )}
      </Box>
      {actions && (
        <Stack
          direction="row"
          spacing={1.5}
          alignItems="center"
          sx={{
            flexWrap: 'wrap',
            rowGap: 1,
            // Buttons sit on a gradient, so give them the same white "gloss" pill as the dashboard quick actions.
            '& .MuiButton-contained, & .MuiButton-outlined': {
              background: 'linear-gradient(180deg,#fff,#EFE3FF)',
              color: 'var(--qs-a, #4a1d86)',
              border: 0,
              boxShadow: '0 6px 14px rgba(0,0,0,.2), inset 0 1px 0 #fff',
              '&:hover': { background: 'linear-gradient(180deg,#fff,#E6D6FF)', transform: 'translateY(-2px)' },
              '&.Mui-disabled': { color: 'rgba(74,29,134,.5)', opacity: 0.8, filter: 'none' },
            },
            '& .MuiIconButton-root': { color: '#fff', bgcolor: 'rgba(255,255,255,.18)' },
          }}
        >
          {actions}
        </Stack>
      )}
    </Stack>
  </Box>
  );
};

export default PageHeader;
